import { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Copy, Download } from 'lucide-react';
import { trpc } from '@/app/lib/trpc';

const messages = defineMessages({
  title: { id: 'studio.inProgress.title', defaultMessage: 'In-Progress Naming Brief' },
  copy: { id: 'studio.inProgress.copy', defaultMessage: 'COPY' },
  download: { id: 'studio.inProgress.download', defaultMessage: 'DOWNLOAD' },
  copied: { id: 'studio.inProgress.copied', defaultMessage: 'Current brief copied.' },
  downloaded: { id: 'studio.inProgress.downloaded', defaultMessage: 'Current brief download started.' },
  failed: { id: 'studio.inProgress.failed', defaultMessage: 'Could not retrieve or save the brief. Please try again.' },
  copyFailed: { id: 'studio.inProgress.copyFailed', defaultMessage: 'Clipboard access was blocked. Please allow clipboard access or download the brief instead.' },
  unavailable: { id: 'studio.inProgress.unavailable', defaultMessage: 'The in-progress brief is no longer available. If you selected a name, use your final Naming Brief.' },
});

/** Reads the current session's server gate, never candidate-card props. Each
 * click reads again so the exported document is not frozen when buttons unlock. */
export function InProgressBriefActions({ generating }: { generating: boolean }) {
  const intl = useIntl();
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<keyof typeof messages | null>(null);

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const result = await trpc.namingBrief.inProgress.query();
        if (!cancelled) setAvailable(result.available);
      } catch {
        // A transient read failure is not evidence that an unlocked engagement
        // re-locked. Clicks always check again before returning any content.
      }
    };
    void refresh();
    const interval = generating ? window.setInterval(refresh, 2500) : undefined;
    window.addEventListener('focus', refresh);
    return () => {
      cancelled = true;
      if (interval !== undefined) window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
    };
  }, [generating]);

  async function save(mode: 'copy' | 'download') {
    setBusy(true);
    setNotice(null);
    try {
      const result = await trpc.namingBrief.inProgress.query();
      setAvailable(result.available);
      if (!result.available || !result.text || !result.html || !result.filename) {
        setNotice('unavailable');
        return;
      }
      if (mode === 'copy') {
        try {
          await navigator.clipboard.writeText(result.text);
          setNotice('copied');
        } catch {
          setNotice('copyFailed');
        }
      } else {
        const url = URL.createObjectURL(new Blob([result.html], { type: 'text/html;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = result.filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        setNotice('downloaded');
      }
    } catch {
      setNotice('failed');
    } finally {
      setBusy(false);
    }
  }

  if (!available && !notice) return null;
  const buttonClass = 'inline-flex min-h-10 items-center gap-2 rounded border border-canvas-ink/40 px-3 py-2 text-xs font-semibold tracking-wide text-canvas-ink hover:bg-canvas-ink/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-canvas-ink disabled:opacity-50';
  return (
    <div className="mt-9 border-t border-canvas-meta/25 pt-5 text-canvas-ink">
      {available ? <>
        <p className="text-xs font-semibold text-canvas-ink">{intl.formatMessage(messages.title)}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" className={buttonClass} disabled={busy} onClick={() => void save('copy')}>
            <Copy size={14} aria-hidden="true" />{intl.formatMessage(messages.copy)}
          </button>
          <button type="button" className={buttonClass} disabled={busy} onClick={() => void save('download')}>
            <Download size={14} aria-hidden="true" />{intl.formatMessage(messages.download)}
          </button>
        </div>
      </> : null}
      {notice ? <p role="status" className="mt-3 max-w-prose text-sm text-canvas-ink">{intl.formatMessage(messages[notice])}</p> : null}
    </div>
  );
}
